import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from '../../../environments/environment';
import { Observable, catchError, map, of, switchMap, take, tap } from "rxjs";
import { IDevice, IDeviceFacilities, IDeviceStructuredResponse } from "../../shared/models/be/device";
import { IMentorageConfigSnapshot } from "../../shared/models/be/mentorageConfig";
import { RequestsService } from "./utils/requests.service";
import { LoggerService } from "./utils/logger.service";
import { IStartStreaming, IStreamingConfig } from "../../shared/models/be/streaming";
import { EventType, EventTypeCodes } from "../../shared/models/be/notification";
import { IAdvancedDevicesSettings, IDevicesSettingsMode, ISimpleDevicesSettings } from "../../shared/models/be/devicesSettings";
import { checkForOverlapsAdvanced, getNotificationSilencingStatus, isActualTimeInSlots } from "../../shared/utils/check.utils";
import { DeviceDiagnosticsFilters, DeviceDiagnosticsResponse } from "../../shared/models/be/deviceDiagnostics";

type NotificationSetting = {
    notificationMode: 'always_active' | 'disabled' | 'scheduled';
    schedules: { start_time: string; end_time: string }[];
};

const isNotificationSettingSilenced = (setting: NotificationSetting): boolean =>
    setting.notificationMode === 'disabled' ||
    (setting.notificationMode === 'scheduled' && !isActualTimeInSlots(setting.schedules));

export interface IGetDevicesParams {
    page?: number;
    limit?: number;
    includeTechnicalDetails?: boolean;
    label?: string;
    customerId?: string;
    organizationId?: string;
    buildingId?: string;
    unitId?: string;
    roomId?: string;
    association?: 'associated' | 'available' | 'associato' | 'disponibile';
    status?: string | string[];
    statuses?: string | string[];
}

@Injectable({
    providedIn: 'any'
})
export class SensorService {

    constructor(
        private _http: HttpClient,
        private requestsService: RequestsService,
        private loggerService: LoggerService
    ) { }

    public getServiceStatus$(): Observable<boolean> {
        return this._http.get<{ message: string }>(`${environment.apiEndpoint}/iot-devices/ping`).pipe(
            take(1),
            map((val: { message: string }) => !!val.message),
            catchError(err => {
                this.loggerService.error('Error on .../ping')
                return of(false);
            })
        );
    }

    /**
     * @description Consente di recuperare i sensori, filtrandone la ricerca per Cliente, Edificio
     */
    public getDevices$(params: IGetDevicesParams): Observable<{ devices: IDevice[], limit: number, page: number, total: number }> {
        // Inizializziamo l'oggetto HttpParams
        let queryParams = new HttpParams();
        // Cicliamo le chiavi dell'oggetto params in ingresso
        Object.entries(params).forEach(([key, value]) => {
            // Se il valore esiste (non è null o undefined), lo aggiungiamo
            if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                    value.forEach((singleValue) => {
                        if (singleValue !== undefined && singleValue !== null && singleValue !== '') {
                            queryParams = queryParams.append(key, String(singleValue));
                        }
                    });
                } else if (value !== '') {
                    queryParams = queryParams.append(key, String(value));
                }
            }
        });
        return this._http.get<{ devices: IDevice[], limit: number, page: number, total: number }>(
            `${environment.apiEndpoint}/iot-devices`,
            { params: queryParams } // Passiamo l'oggetto queryParams nelle options della GET
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        );
    }

    public getDevicesByRoomId$(roomId: string): Observable<IDevice[]> {
        const currentUser = this.requestsService.getCurrentUser();
        const customerId = currentUser?.customer?.id ? `?customerId=${currentUser?.customer?.id}` : '';
        return this._http.get<{ devices: IDevice[] }>(
            `${environment.apiEndpoint}/iot-devices${customerId}`
        ).pipe(
            take(1),
            map((data) => data.devices.filter((dev) => dev.facilities?.room?.id === roomId)),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public getDevice$(deviceId: string): Observable<IDevice> {
        return this._http.get<IDevice>(
            `${environment.apiEndpoint}/iot-devices/${deviceId}`
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public getMentorageConfig$(deviceId: string): Observable<IMentorageConfigSnapshot> {
        return this._http.get<IMentorageConfigSnapshot>(
            `${environment.apiEndpoint}/iot-devices/${deviceId}/mentorage-config`
        ).pipe(
            take(1),
            catchError(err => {
                this.loggerService.error('Errore nel recupero della configurazione Mentorage');
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public updateMentorageConfig$(deviceId: string, payload: { config: any }): Observable<any> {
        return this._http.patch<any>(
            `${environment.apiEndpoint}/iot-devices/${deviceId}/mentorage-config`, payload
        ).pipe(
            take(1),
            catchError(err => {
                this.loggerService.error('Errore nell\'aggiornamento della configurazione Mentorage');
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public linkDevice$(deviceId: string, deviceFacilities?: IDeviceFacilities & { customer?: { id: string; name: string } }): Observable<IDevice> {
        const currentUser = this.requestsService.getCurrentUser();
        deviceFacilities && (deviceFacilities['customer'] = {
            id: String(currentUser?.customer?.id),
            name: String(currentUser?.customer?.businessName)
        });
        deviceFacilities && (deviceFacilities['organization'] = {
            id: String(currentUser?.organization?.id),
            name: String(currentUser?.organization?.businessName)
        });
        return this._http.put<IDevice>(
            `${environment.apiEndpoint}/iot-devices/${deviceId}/facilities`, deviceFacilities || {}
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public deleteDevice$(deviceLabel: string) {
        return this._http.delete(
            `${environment.apiEndpoint}/iot-devices/${deviceLabel}`
        ).pipe(
            tap(() => {
            }),
            catchError(err => {
                throw `error in source. Details:  + ${err}`
            })
        );
    }

    public disableDevice$(deviceId: string): Observable<{ success: boolean; deviceId: string; status: string; label: string }> {
        return this._http.put<{ success: boolean; deviceId: string; status: string; label: string }>(
            `${environment.apiEndpoint}/iot-devices/${deviceId}/disable`, {}
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public enableDevice$(deviceId: string): Observable<{ success: boolean; deviceId: string; status: string; label: string }> {
        return this._http.put<{ success: boolean; deviceId: string; status: string; label: string }>(
            `${environment.apiEndpoint}/iot-devices/${deviceId}/enable`, {}
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public editDeviceName$(deviceNameData: { oldLabel: string, newLabel: string }): Observable<{ success: boolean; deviceId: string; status: string; label: string }> {
        return this._http.put<{ success: boolean; deviceId: string; status: string; label: string }>(
            `${environment.apiEndpoint}/iot-devices/label`, deviceNameData
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public getSensorsTemperature$(threshold: number) {
        return this._http.get<IDeviceStructuredResponse>(
            `${environment.apiEndpoint}/iot-devices/temperature/above?threshold=${threshold}`
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public getDeviceDiagnostics$(filters: DeviceDiagnosticsFilters = {}): Observable<DeviceDiagnosticsResponse> {
        let queryParams = new HttpParams();

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams = queryParams.set(key, String(value));
            }
        });

        return this._http.get<DeviceDiagnosticsResponse>(
            `${environment.apiEndpoint}/iot-devices/diagnostics`,
            { params: queryParams }
        ).pipe(take(1));
    }

    public sendEvent$(deviceId: string, eventType: EventTypeCodes, buildingId?: string, bed_id_mentorage?: string) {
        const currentUser = this.requestsService.getCurrentUser();
        return this._http.post<any>(
            `${environment.apiEndpoint}/notifications/building/${buildingId || currentUser?.building?.id}/notify`, {
            title: "Simulazione Notifica",
            body: EventType[eventType],
            eventType,
            data: {
                deviceId,
                bed_id_mentorage
            }
        }
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }
    public getDeviceSettingsMode$(roomId: string) {
        const currentUser = this.requestsService.getCurrentUser();
        return this._http.get<IDevicesSettingsMode>(
            `${environment.apiEndpoint}/notifications/scheduling/room/${roomId}/status?buildingId=${currentUser?.building?.id}`
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public getSimpleDevicesSettings$(roomId: string) {
        const currentUser = this.requestsService.getCurrentUser();
        return this._http.get<ISimpleDevicesSettings>(
            `${environment.apiEndpoint}/notifications/scheduling/simple/room/${roomId}?buildingId=${currentUser?.building?.id}`
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public getAdvancedDevicesSettings$(roomId: string) {
        const currentUser = this.requestsService.getCurrentUser();
        return this._http.get<IAdvancedDevicesSettings>(
            `${environment.apiEndpoint}/notifications/scheduling/advanced/room/${roomId}?buildingId=${currentUser?.building?.id}`
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public editSimpleDevicesSettings$(devicesSetting: ISimpleDevicesSettings) {
        const currentUser = this.requestsService.getCurrentUser();
        devicesSetting = {
            ...devicesSetting,
            buildingId: currentUser!.building!.id
        }
        return this._http.put<any>(
            `${environment.apiEndpoint}/notifications/scheduling/simple/room/${devicesSetting.roomId}`, devicesSetting
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public editAdvancedDevicesSettings$(devicesSetting: IAdvancedDevicesSettings) {
        const currentUser = this.requestsService.getCurrentUser();
        devicesSetting = {
            ...devicesSetting,
            buildingId: currentUser!.building!.id
        }
        return this._http.put<any>(
            `${environment.apiEndpoint}/notifications/scheduling/advanced/room/${devicesSetting.roomId}`, devicesSetting
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    }

    public isSensorSilenced$(roomId: string): Observable<{
        fall: boolean;
        room: boolean;
        bathroom: boolean;
        bed: boolean;
        status: 'active' | 'partial' | 'silenced';
        bedExitDelayMinutes: number;
    }> {
        return this.getDeviceSettingsMode$(roomId).pipe(
            switchMap((settingsMode) => {
                if (settingsMode.currentMode === 'simple') {
                    if (settingsMode.hasSettings) {
                        return this.getSimpleDevicesSettings$(roomId).pipe(
                            map((data) => {
                                const isSilenced = isNotificationSettingSilenced(data.settings);
                                return {
                                    fall: isSilenced,
                                    room: isSilenced,
                                    bathroom: isSilenced,
                                    bed: isSilenced,
                                    status: getNotificationSilencingStatus([isSilenced]),
                                    bedExitDelayMinutes: data.settings.bedExitDelay
                                }
                            })
                        );
                    } else {
                        return of({
                            fall: false,
                            room: false,
                            bathroom: false,
                            bed: false,
                            status: 'active' as const,
                            bedExitDelayMinutes: 0
                        })
                    }
                } else if (settingsMode.currentMode === 'advanced') {
                    return this.getAdvancedDevicesSettings$(roomId).pipe(
                        map((data) => {
                            const fallSilenced = isNotificationSettingSilenced(data.settings.fall);
                            const roomSilenced = isNotificationSettingSilenced(data.settings.room_events);
                            const bathroomEvents = data.settings.bathroom_events ?? {
                                notificationMode: 'always_active' as const,
                                bathroomEntranceDelay: 0,
                                schedules: []
                            };
                            const bathroomSilenced = isNotificationSettingSilenced(bathroomEvents);
                            const bedSilencedStates = data.beds.map((bed) =>
                                isNotificationSettingSilenced(data.settings.bed_events[bed.bedId])
                            );
                            const silencedCategories = [fallSilenced, roomSilenced, bathroomSilenced, ...bedSilencedStates];

                            return {
                                fall: fallSilenced,
                                room: roomSilenced,
                                bathroom: bathroomSilenced,
                                bed: bedSilencedStates.some(Boolean),
                                status: getNotificationSilencingStatus(silencedCategories),
                                bedExitDelayMinutes: 0
                            }
                        })
                    );
                } else {
                    return of({
                        fall: false,
                        room: false,
                        bathroom: false,
                        bed: false,
                        status: 'active' as const,
                        bedExitDelayMinutes: 0
                    });
                }
            })
        )
    }
}
